import { swalFireError } from "../../admin/sweet-alert.js";

let googlepayServicesConfig = {};
let googlepayJqForm = null;

/**
 * Define the version of the Google Pay API referenced when creating your
 * configuration
 *
 * @see {@link https://developers.google.com/pay/api/web/reference/request-objects#PaymentDataRequest|apiVersion in PaymentDataRequest}
 */
let baseRequest = {
    apiVersion: 2,
    apiVersionMinor: 0,
    emailRequired: true,
};

/**
 * Card networks supported by your site and your gateway
 *
 * @see {@link https://developers.google.com/pay/api/web/reference/request-objects#CardParameters|CardParameters}
 * @todo confirm card networks supported by your site and gateway
 */
let allowedCardNetworks = [
    "AMEX",
    "DISCOVER",
    "INTERAC",
    "JCB",
    "MASTERCARD",
    "MIR",
    "VISA",
];

/**
 * Card authentication methods supported by your site and your gateway
 *
 * @see {@link https://developers.google.com/pay/api/web/reference/request-objects#CardParameters|CardParameters}
 * @todo confirm your processor supports Android device tokens for your
 * supported card networks
 */
let allowedCardAuthMethods = ["PAN_ONLY", "CRYPTOGRAM_3DS"];

/**
 * Identify your gateway and your site's gateway merchant identifier
 *
 * The Google Pay API response will return an encrypted payment method capable
 * of being charged by a supported gateway after payer authorization
 *
 * @todo check with your gateway on the parameters to pass
 * @see {@link https://developers.google.com/pay/api/web/reference/request-objects#gateway|PaymentMethodTokenizationSpecification}
 */
let tokenizationSpecification = {
    type: "PAYMENT_GATEWAY",
    parameters: {
        gateway: "crst",
        gatewayMerchantId: "",
    },
};

/**
 * Describe your site's support for the CARD payment method and its required
 * fields
 *
 * @see {@link https://developers.google.com/pay/api/web/reference/request-objects#CardParameters|CardParameters}
 */
let baseCardPaymentMethod = {
    type: "CARD",
    parameters: {
        allowedAuthMethods: allowedCardAuthMethods,
        allowedCardNetworks: allowedCardNetworks,
        billingAddressRequired: true,
        billingAddressParameters: {
            format: "FULL",
        },
    },
};

/**
 * Describe your site's support for the CARD payment method including optional
 * fields
 *
 * @see {@link https://developers.google.com/pay/api/web/reference/request-objects#CardParameters|CardParameters}
 */
let cardPaymentMethod = Object.assign({}, baseCardPaymentMethod, {
    tokenizationSpecification: tokenizationSpecification,
});

/**
 * An initialized google.payments.api.PaymentsClient object or null if not yet set
 *
 * @see {@link getGooglePaymentsClient}
 */
let paymentsClient = null;

document.addEventListener("DOMContentLoaded", () => {
    const {
        isGooglePayAuthJwtLoaded,
        isGooglePayLoaded,
        googlePayConfig,
        blinkPageInfo,
        blinkCurrencyList,
        merchantName,
        authJwtToken,
    } = window.config;

    console.group("Google Pay Debug");
    console.table({
        merchantName: blinkPageInfo?.merchantName,
        googleMerchantID: blinkPageInfo?.googleMerchantID,
        origin: window.location.origin,
        environment: googlePayConfig?.environment,
        authJwtPresent: !!googlePayConfig?.authJwt,
    });
    console.log("Full googlePayConfig:", googlePayConfig);
    console.log("Full blinkPageInfo:", blinkPageInfo);
    console.groupEnd();

    if (isGooglePayAuthJwtLoaded) {
        onGooglePayAuthJwtLoaded(
            "form1",
            googlePayConfig,
            blinkPageInfo,
            merchantName,
            authJwtToken,
        );
    } else if (isGooglePayLoaded) {
        onGooglePayLoaded("form1", googlePayConfig, blinkPageInfo);
    }

    /**
     * Configure your site's support for payment methods supported by the Google Pay
     * API.
     *
     * Each member of allowedPaymentMethods should contain only the required fields,
     * allowing reuse of this base request when determining a viewer's ability
     * to pay and later requesting a supported payment method
     *
     * @returns {object} Google Pay API version, payment methods supported by the site
     */
    function getGoogleIsReadyToPayRequest() {
        return Object.assign({}, baseRequest, {
            allowedPaymentMethods: [baseCardPaymentMethod],
        });
    }

    /**
     * Configure support for the Google Pay API
     *
     * @see {@link https://developers.google.com/pay/api/web/reference/request-objects#PaymentDataRequest|PaymentDataRequest}
     * @returns {object} PaymentDataRequest fields
     */
    function getGooglePaymentDataRequest() {
        const paymentDataRequest = Object.assign({}, baseRequest);

        paymentDataRequest.allowedPaymentMethods = [cardPaymentMethod];
        paymentDataRequest.transactionInfo = getGoogleTransactionInfo();

        if (
            googlepayServicesConfig.authJwt &&
            typeof googlepayServicesConfig.authJwt == "string"
        ) {
            paymentDataRequest.merchantInfo = {
                // @todo a merchant ID is available for a production environment after approval by Google
                // See {@link https://developers.google.com/pay/api/web/guides/test-and-deploy/integration-checklist|Integration checklist}
                merchantId:
                    googlepayServicesConfig.merchantInfo.googleMerchantID,
                merchantOrigin: location.host, // Required for JWT
                merchantName: googlepayServicesConfig.googleMerchantName,
                authJwt: googlepayServicesConfig.authJwt, // Required for JWT
            };
        } else {
            paymentDataRequest.merchantInfo = {
                // @todo a merchant ID is available for a production environment after approval by Google
                // See {@link https://developers.google.com/pay/api/web/guides/test-and-deploy/integration-checklist|Integration checklist}
                merchantId:
                    googlepayServicesConfig.merchantInfo.googleMerchantID,
                merchantName:
                    googlepayServicesConfig.merchantInfo.merchantName +
                    " | Blinkpayment",
            };
        }

        return paymentDataRequest;
    }

    /**
     * Return an active PaymentsClient or initialize
     *
     * @see {@link https://developers.google.com/pay/api/web/reference/client#PaymentsClient|PaymentsClient constructor}
     * @returns {google.payments.api.PaymentsClient} Google Pay API client
     */
    function getGooglePaymentsClient() {
        if (paymentsClient === null) {
            paymentsClient = new google.payments.api.PaymentsClient({
                environment:
                    googlepayServicesConfig &&
                    googlepayServicesConfig.environment
                        ? googlepayServicesConfig.environment
                        : "TEST",
            }); // 'TEST | PRODUCTION'
        }

        return paymentsClient;
    }

    /**
     * Initialize Google PaymentsClient after Google-hosted JavaScript has loaded
     *
     * Display a Google Pay payment button after confirmation of the viewer's
     * ability to pay.
     */
    function onGooglePayLoaded(
        formId,
        blinkServicesGooglePayConfig,
        blinkPageInfo,
    ) {
        googlepayJqForm = $("#" + formId);

        if (blinkServicesGooglePayConfig) {
            googlepayServicesConfig = blinkServicesGooglePayConfig;

            if (blinkServicesGooglePayConfig.apiVersion) {
                baseRequest.apiVersion = parseInt(
                    blinkServicesGooglePayConfig.apiVersion,
                );
            }
            if (blinkServicesGooglePayConfig.apiVersionMinor) {
                baseRequest.apiVersionMinor = parseInt(
                    blinkServicesGooglePayConfig.apiVersionMinor,
                );
            }

            if (blinkServicesGooglePayConfig.allowedCardNetworks) {
                allowedCardNetworks =
                    blinkServicesGooglePayConfig.allowedCardNetworks;
            }
            if (blinkServicesGooglePayConfig.allowedCardAuthMethods) {
                allowedCardAuthMethods =
                    blinkServicesGooglePayConfig.allowedCardAuthMethods;
            }
        }

        if (blinkPageInfo) {
            googlepayServicesConfig.merchantInfo = blinkPageInfo;

            tokenizationSpecification.parameters.gatewayMerchantId =
                googlepayServicesConfig.merchantInfo.merchantID.toString();
        }

        baseCardPaymentMethod = {
            type: "CARD",
            parameters: {
                allowedAuthMethods: allowedCardAuthMethods,
                allowedCardNetworks: allowedCardNetworks,
                billingAddressRequired:
                    baseCardPaymentMethod.parameters.billingAddressRequired,
                billingAddressParameters:
                    baseCardPaymentMethod.parameters.billingAddressParameters,
            },
        };

        cardPaymentMethod = Object.assign({}, baseCardPaymentMethod, {
            tokenizationSpecification: tokenizationSpecification,
        });

        const paymentsClient = getGooglePaymentsClient();

        paymentsClient
            .isReadyToPay(getGoogleIsReadyToPayRequest())
            .then(function (response) {
                if (response.result) {
                    $("#linkPayByGooglePay").on(
                        "click",
                        onGooglePaymentButtonClicked,
                    );
                }
            })
            .catch(function (err) {
                // show error in developer console for debugging
                console.error("onGooglePayLoaded >> err: ", err);

                $("#linkPayByGooglePay").data("err", err);
                $("#linkPayByGooglePay").on(
                    "click",
                    onErrorGooglePaymentButtonClicked,
                );
            });
    }

    /**
     * Initialize Google PaymentsClient after Google-hosted JavaScript has loaded
     *
     * Display a Google Pay payment button after confirmation of the viewer's
     * ability to pay.
     */
    function onGooglePayAuthJwtLoaded(
        formId,
        blinkServicesGooglePayConfig,
        blinkPageInfo,
        googleMerchantName,
        authJwtToken,
    ) {
        googlepayJqForm = $("#" + formId);

        if (blinkServicesGooglePayConfig) {
            googlepayServicesConfig = blinkServicesGooglePayConfig;

            googlepayServicesConfig.authJwt = authJwtToken;
            googlepayServicesConfig.googleMerchantName = googleMerchantName;

            if (blinkServicesGooglePayConfig.apiVersion) {
                baseRequest.apiVersion = parseInt(
                    blinkServicesGooglePayConfig.apiVersion,
                );
            }
            if (blinkServicesGooglePayConfig.apiVersionMinor) {
                baseRequest.apiVersionMinor = parseInt(
                    blinkServicesGooglePayConfig.apiVersionMinor,
                );
            }

            if (blinkServicesGooglePayConfig.allowedCardNetworks) {
                allowedCardNetworks =
                    blinkServicesGooglePayConfig.allowedCardNetworks;
            }
            if (blinkServicesGooglePayConfig.allowedCardAuthMethods) {
                allowedCardAuthMethods =
                    blinkServicesGooglePayConfig.allowedCardAuthMethods;
            }
        }

        if (blinkPageInfo) {
            googlepayServicesConfig.merchantInfo = blinkPageInfo;

            tokenizationSpecification.parameters.gatewayMerchantId =
                googlepayServicesConfig.merchantInfo.merchantID.toString();
        }

        baseCardPaymentMethod = {
            type: "CARD",
            parameters: {
                allowedAuthMethods: allowedCardAuthMethods,
                allowedCardNetworks: allowedCardNetworks,
                billingAddressRequired:
                    baseCardPaymentMethod.parameters.billingAddressRequired,
                billingAddressParameters:
                    baseCardPaymentMethod.parameters.billingAddressParameters,
            },
        };

        cardPaymentMethod = Object.assign({}, baseCardPaymentMethod, {
            tokenizationSpecification: tokenizationSpecification,
        });

        const paymentsClient = getGooglePaymentsClient();

        paymentsClient
            .isReadyToPay(getGoogleIsReadyToPayRequest())
            .then(function (response) {
                if (response.result) {
                    $("#linkPayByGooglePay").on(
                        "click",
                        onGooglePaymentButtonClicked,
                    );
                }
            })
            .catch(function (err) {
                // show error in developer console for debugging
                console.error(err);

                $("#linkPayByGooglePay").data("err", err);
                $("#linkPayByGooglePay").on(
                    "click",
                    onErrorGooglePaymentButtonClicked,
                );
            });
    }

    /**
     * Provide Google Pay API with a payment amount, currency, and amount status
     *
     * @see {@link https://developers.google.com/pay/api/web/reference/request-objects#TransactionInfo|TransactionInfo}
     * @returns {object} transaction info, suitable for use as transactionInfo property of PaymentDataRequest
     */
    function getGoogleTransactionInfo() {
        return {
            countryCode: "GB",
            currencyCode:
                blinkCurrencyList[
                    googlepayJqForm.find("[name=currencyCode]").val()
                ].text, //'GBP'
            totalPriceStatus: "FINAL",
            // set to cart total
            totalPrice: $("#rawAmount").val(),
        };
    }

    /**
     * Prefetch payment data to improve performance
     *
     * @see {@link https://developers.google.com/pay/api/web/reference/client#prefetchPaymentData|prefetchPaymentData()}
     */
    function prefetchGooglePaymentData() {
        const paymentDataRequest = getGooglePaymentDataRequest();

        // transactionInfo must be set but does not affect cache
        paymentDataRequest.transactionInfo = {
            totalPriceStatus: "NOT_CURRENTLY_KNOWN",
            currencyCode:
                blinkCurrencyList[
                    googlepayJqForm.find("[name=currencyCode]").val()
                ].text, //'GBP'
        };

        const paymentsClient = getGooglePaymentsClient();
        paymentsClient.prefetchPaymentData(paymentDataRequest);
    }

    /**
     * Show Google Pay payment sheet when Google Pay payment button is clicked
     */
    function onGooglePaymentButtonClicked() {
        var requiredErrors = [];

        googlepayJqForm
            .find(":required")
            .not("[name=nameon_card],[name=transactionUnique]")
            .not("[type^='hostedfield:']")
            .not("#card_payment input")
            .each(function () {
                if ("" == $(this).val().trim()) {
                    var label =
                        this.placeholder ||
                        $(this).siblings(".holder-txt").text().trim() ||
                        $(this)
                            .closest(".input-wrapper")
                            .find("label")
                            .text()
                            .trim() ||
                        this.name;
                    requiredErrors.push(label.replace(/\*$/, "").trim());
                }
            });

        if ($("#gift_aid_display").is(":checked")) {
            googlepayJqForm
                .find(":input.dev-form-control-gift-aid")
                .each(function () {
                    if (false == $(this).prop("disabled")) {
                        if ("" == $(this).val().trim()) {
                            requiredErrors.push("Gift Aid " + this.placeholder);
                        }
                    }
                });
        }

        if (requiredErrors.length) {
            return swalFireError(
                '<ul class="list-unstyled text-danger"><li>' +
                    requiredErrors.join("</li><li>") +
                    "</li></ul>",
                "Please fill required fields",
            );
        }

        const paymentDataRequest = getGooglePaymentDataRequest();
        paymentDataRequest.transactionInfo = getGoogleTransactionInfo();

        const paymentsClient = getGooglePaymentsClient();
        paymentsClient
            .loadPaymentData(paymentDataRequest)
            .then(function (paymentData) {
                // handle the response
                processPayment(paymentData);
            })
            .catch(function (err) {
                // show error in developer console for debugging
                console.error(err);
                //swalFireError(err);
            });
    }

    /**
     * Show Google Pay Error when Google Pay payment button is clicked
     */
    function onErrorGooglePaymentButtonClicked() {
        swalFireError($(this).data("err"));
    }

    /**
     * Process payment data returned by the Google Pay API
     *
     * @param {object} paymentData response from Google Pay API after user approves payment
     * @see {@link https://developers.google.com/pay/api/web/reference/response-objects#PaymentData|PaymentData object reference}
     */
    function processPayment(paymentData) {
        // show returned data in developer console for debugging
        //const {description} = paymentData.paymentMethodData;
        //document.getElementById("response").innerHTML = `Payment Succesful with ${description}`;

        // @todo pass payment token to your gateway to process payment
        //paymentToken = paymentData.paymentMethodData.tokenizationData.token;

        blinkProcessPayment(paymentData);
    }

    function blinkProcessPayment(paymentData) {
        var $modalPaymentProccessing = $("#payment-processing-modal");
        var hiddenInputs = [];

        $("#payment-processing-modal").find("form").remove();

        hiddenInputs.push(
            '<input type="hidden" name="paymentMethod" value="googlepay">',
        );
        //hiddenInputs.push('<input type="hidden" name="paymentToken" value="' + paymentData.paymentMethodData.tokenizationData.token + '">');
        hiddenInputs.push('<input type="hidden" name="paymentToken" value="">');

        googlepayJqForm.find(":input").each(function () {
            var $self = $(this);

            if ("" != $self.prop("name")) {
                if (
                    $self.prop("name") == "customerAddress" &&
                    $self.val().length == 0
                ) {
                    var addressDataArray = [
                        paymentData?.paymentMethodData?.info?.billingAddress
                            ?.address1,
                        paymentData?.paymentMethodData?.info?.billingAddress
                            ?.address2,
                        paymentData?.paymentMethodData?.info?.billingAddress
                            ?.address3,
                        paymentData?.paymentMethodData?.info?.billingAddress
                            ?.administrativeArea,
                        paymentData?.paymentMethodData?.info?.billingAddress
                            ?.locality,
                        paymentData?.paymentMethodData?.info?.billingAddress
                            ?.countryCode,
                    ];
                    $self.val(
                        addressDataArray
                            .filter((value) => {
                                return (
                                    value !== "" &&
                                    value !== null &&
                                    value !== undefined &&
                                    !Number.isNaN(value) &&
                                    value.length > 2
                                );
                            })
                            .join(","),
                    );
                }

                if (
                    $self.prop("name") == "customerPostCode" &&
                    $self.val().length == 0 &&
                    paymentData?.paymentMethodData?.info?.billingAddress
                        ?.postalCode.length > 0
                ) {
                    $self.val(
                        paymentData.paymentMethodData.info.billingAddress
                            .postalCode,
                    );
                }

                if (
                    $self.prop("name") == "nameon_card" &&
                    $self.val().length == 0 &&
                    paymentData?.paymentMethodData?.info?.billingAddress?.name
                        .length > 0
                ) {
                    $self.val(
                        paymentData.paymentMethodData.info.billingAddress.name,
                    );
                }

                if (
                    $self.prop("name") == "customerName" &&
                    $self.val().length == 0 &&
                    paymentData?.paymentMethodData?.info?.billingAddress?.name
                        .length > 0
                ) {
                    $self.val(
                        paymentData.paymentMethodData.info.billingAddress.name,
                    );
                }

                if (
                    $self.prop("name") == "customerEmail" &&
                    $self.val().length == 0 &&
                    paymentData?.paymentMethodData?.info?.billingAddress?.name
                        .length > 0
                ) {
                    $self.val(paymentData.email);
                }

                hiddenInputs.push(
                    '<input type="hidden" name="' +
                        $self.prop("name") +
                        '" value="' +
                        $self.val() +
                        '">',
                );
            }
        });

        $modalPaymentProccessing
            .find(".modal-body")
            .append(
                '<form action="' +
                    googlepayJqForm.prop("action") +
                    '" method="' +
                    googlepayJqForm.prop("method") +
                    '">' +
                    hiddenInputs.join("\r\n") +
                    "</form>",
            );

        $("#payment-processing-modal").find(
            "input[name=paymentToken]",
        )[0].value = paymentData.paymentMethodData.tokenizationData.token;

        new bootstrap.Modal($modalPaymentProccessing[0]).show();

        /**/
        setTimeout(function () {
            $modalPaymentProccessing.find("form").submit();
        }, 1000);
        /**/
    }
});
